import { Flex, Text, VStack } from '@chakra-ui/react';
import { Board, Column } from '@gamma/ui/layout';
import { Alert } from '@gamma/ui/overlay';
import { NavTab, NavTabs } from '@gamma/ui/tabs';
import { PageHeader } from '../../../shared/components';
import { i18n } from '../../../shared/localization';
import { useContext } from 'react';
import { Outlet, useParams } from 'react-router';
import { ROUTES } from '../../../shared/constants/routes';
import { AuthContext } from '../../../shared/providers/AuthProvider/AuthContext';
import {
  useQueryEscalationConfigurations,
  useQueryEscalationConfigurationsPublic,
} from '../queries';
import { AppConfigurationsContext } from '../../../shared/providers';

const { system, backToIntegrations } = i18n.navigation;
const { analystContactAdmin } = i18n.pages.integrations;

export interface OutletContextType {
  configsLoading: boolean;
  featureFlags: Record<string, boolean>;
}

export const Integrations = () => {
  const tabs: NavTab[] = [
    {
      title: system.integrations,
      to: ROUTES.systemSettingsIntegrationsConfig,
      isDisabled: false,
    },
    {
      title: system.alertExports,
      to: ROUTES.systemSettingsIntegrationsAlertExports,
      isDisabled: false,
    },
  ];

  const { integration } = useParams();

  const {
    userRole,
    setUserPrivileges,
    userPrivileges,
    userPrivilegesLoading,
    userLicense,
  } = useContext(AuthContext);

  const { appConfigurations, appConfigurationsLoading } = useContext(
    AppConfigurationsContext,
  );
  const { tenant = '' } = userLicense ?? {};

  const serviceNowConfigurationQuery =
    userRole === 'admin'
      ? useQueryEscalationConfigurations
      : useQueryEscalationConfigurationsPublic;

  const { loading: serviceNowLoading } = serviceNowConfigurationQuery({
    onCompleted: (data) => {
      if (data) {
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? '']['service-now'] =
          data?.queryEscalationConfigurations?.escalation_configurations?.[0]
            ?.enabled ?? false;
        setUserPrivileges(newUserPrivileges);
      }
    },
  });

  const featureFlags = appConfigurations?.feature_flags ?? {};

  const configsLoading =
    userPrivilegesLoading || serviceNowLoading || appConfigurationsLoading;

  return (
    <Board>
      <Column>
        {integration ? (
          <PageHeader
            prevPage={ROUTES.systemSettingsIntegrationsConfig}
            breadcrumbs={[
              {
                label: (
                  <Flex alignItems="center" justifyContent="center">
                    <Text>{backToIntegrations}</Text>
                  </Flex>
                ),
                link: ROUTES.systemSettingsIntegrationsConfig,
              },
            ]}
          />
        ) : (
          <>
            <PageHeader>{system.integrations}</PageHeader>
            <VStack>
              {userRole !== 'admin' && (
                <Alert status="warning">{analystContactAdmin}</Alert>
              )}
              <NavTabs
                tabs={tabs}
                isFitted={false}
                variant="line"
                basePath="/"
              />
            </VStack>
          </>
        )}
      </Column>
      <Column
        padding={0}
        flexGrow={1}
        display="flex"
        flexBasis="auto"
        flexDirection="column"
      >
        <Outlet
          context={{
            configsLoading,
            featureFlags,
          }}
        />
      </Column>
    </Board>
  );
};
